import { baseApi } from './baseApi';
import { FolderManagerItemActionNames, FolderManagerItemActionTypes, FolderManagerMimeTypes } from '../interfaces';
import { ServiceTypes } from '../constants';
import { prepareHeaders } from './helpers';

export const mapApiToStore = (item: SlideFromApi): FolderManagerItem<SlideFromApi> | null => {
  if (!item?.AssetVersionId) {
    console.error('Could not parse search slide results: invalid slide format!');
    return null;
  }

  return {
    id: `${item.AssetVersionId}-${item.SlideNumber}`,
    displayName: item.Title,
    type: FolderManagerMimeTypes.SLIDE,
    thumbnailUrl: item?.ThumbnailLocation,
    itemData: item,
    actions: [
      {
        name: FolderManagerItemActionNames.ADD_TO_PRESENTATION,
        type: FolderManagerItemActionTypes.MULTI,
      },
    ],
  } as any;
};

export const slidesService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSlidesBySearch: builder.query<FolderManagerItem[], { query: string }>({
      query: ({ query }) => ({
        url: `/slides${query ? `?query=${query}` : ''}`,
      }),
      providesTags: () => [{ type: ServiceTypes.SLIDES, id: 'SEARCH' }],
      transformResponse: (result: INgApiResponse<SlideFromApi[]>): FolderManagerItem[] =>
        (result?.data || [])
          .map(mapApiToStore)
          .filter((item): item is FolderManagerItem<SlideFromApi> => item !== null),
    }),
    getSlideThumbnail: builder.query<
      { url: string; isGenerating: boolean },
      { url?: string | null; themeId?: string | number | null }
    >({
      queryFn: async ({ url, themeId }, api) => {
        if (!url) {
          return { error: { status: 400, data: 'URL is required' } };
        }

        const result = await fetch(`${url}${themeId ? `?theme_id=${themeId}` : ''}`, {
          headers: prepareHeaders(new Headers(), api),
          signal: api.signal,
        });

        if (!result.ok) {
          return { error: { status: result.status, data: await result.text() } };
        }

        const blob = await result.blob();
        const objectUrl = URL.createObjectURL(blob);

        return { data: { url: objectUrl, isGenerating: result.status === 202 } };
      },
      providesTags: (_res, _err, { url, themeId }) => [
        { type: ServiceTypes.SLIDES, id: `THUMBNAIL_${url}_${themeId ?? 'default'}` },
      ],
    }),
  }),
});

export const { useGetSlidesBySearchQuery, useGetSlideThumbnailQuery } = slidesService;
